import { Link, useSidebarOpenState } from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { SettingsIcon } from '../../components/icons';
import { useMyTeams } from './useMyTeams';

const useStyles = makeStyles({
  root: {
    // The sidebar column is align-items:flex-start, so stretch to fill the
    // drawer width; otherwise the row shrinks to content and the border-top and
    // settings gear stop short of the edge.
    alignSelf: 'stretch',
    boxSizing: 'border-box',
    // Breathing room between the last nav item and the footer separator.
    marginTop: 14,
    borderTop: '1px solid var(--bui-border-1)',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    textDecoration: 'none',
    color: 'inherit',
  },
  collapsed: {
    justifyContent: 'center',
    padding: '12px 0',
  },
  // Charcoal disc with the user's initials in pale yellow, per the sidebar spec.
  avatar: {
    flex: 'none',
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'var(--hone-portal-surface-solid)',
    color: 'var(--helix-colors-brand-50)',
    fontSize: 11,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    letterSpacing: '0.01em',
  },
  text: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.25,
  },
  name: {
    fontSize: 12.5,
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  team: {
    fontSize: 11,
    color: 'var(--bui-fg-secondary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  settings: {
    flex: 'none',
    width: 15,
    height: 15,
    opacity: 0.5,
  },
});

function initialsFrom(label: string): string {
  const words = label.trim().split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) {
    return '?';
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

function useDisplayName(): string | undefined {
  const identityApi = useApi(identityApiRef);
  const [name, setName] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const profile = await identityApi.getProfileInfo();
      const { userEntityRef } = await identityApi.getBackstageIdentity();
      const fallback = userEntityRef.split('/').pop();
      if (!cancelled) {
        setName(profile.displayName ?? profile.email?.split('@')[0] ?? fallback);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  return name;
}

// The sidebar's bottom identity card: avatar + name + primary team, linking to
// user settings. Geometry follows PortalSidebar.dc.html; colours come from the
// Helix ramp. Collapses to the avatar alone when the sidebar is closed.
export function SidebarUserFooter() {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const displayName = useDisplayName();
  const { teams } = useMyTeams();

  if (!displayName) {
    return null;
  }

  const team = teams[0]?.title;

  return (
    <Link
      to="/settings"
      underline="none"
      className={`${classes.root} ${isOpen ? '' : classes.collapsed}`}
      aria-label="User settings"
    >
      <span className={classes.avatar}>{initialsFrom(displayName)}</span>
      {isOpen && (
        <>
          <span className={classes.text}>
            <span className={classes.name}>{displayName}</span>
            {team && <span className={classes.team}>{team}</span>}
          </span>
          <SettingsIcon className={classes.settings} />
        </>
      )}
    </Link>
  );
}
